import React, { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import toast from 'react-hot-toast';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import * as marketingService from '../../services/marketingService';

const { FiSearch, FiFilter, FiEye, FiChevronDown } = FiIcons;

const STATUS_OPTIONS = [
  { value: '', label: 'All statuses' },
  { value: 'sent', label: 'Sent' },
  { value: 'opened', label: 'Opened' },
  { value: 'clicked', label: 'Clicked' },
  { value: 'failed', label: 'Failed' },
];

const formatDate = (d) => {
  if (!d) return '—';
  const date = new Date(d);
  return date.toLocaleString();
};

const MarketingEmailLogs = () => {
  const [logs, setLogs] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [selectedLog, setSelectedLog] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [page, setPage] = useState(0);
  const pageSize = 20;

  const loadLogs = useCallback(async () => {
    setLoading(true);
    const filters = [];
    if (statusFilter) filters.push({ column: 'status', operator: 'eq', value: statusFilter });
    if (dateFrom) filters.push({ column: 'sent_at', operator: 'gte', value: dateFrom });
    if (dateTo) filters.push({ column: 'sent_at', operator: 'lte', value: dateTo + 'T23:59:59Z' });

    const { data, error, count } = await marketingService.getEmailLogs({
      filters: filters.length ? filters : undefined,
      search: search || undefined,
      orderBy: { column: 'sent_at', ascending: false },
      range: { from: page * pageSize, to: page * pageSize + pageSize - 1 },
    });
    setLoading(false);
    if (error) {
      toast.error(error.message ?? 'Failed to load logs');
      setLogs([]);
      setTotal(0);
      return;
    }
    setLogs(Array.isArray(data) ? data : []);
    setTotal(count ?? 0);
  }, [search, statusFilter, dateFrom, dateTo, page]);

  useEffect(() => {
    loadLogs();
  }, [loadLogs]);

  const openDetail = async (logId) => {
    setDetailLoading(true);
    const { data, error } = await marketingService.getEmailLogById(logId);
    setDetailLoading(false);
    if (error) {
      toast.error(error.message ?? 'Failed to load details');
      return;
    }
    setSelectedLog(data);
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className="space-y-6"
    >
      <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
        <h3 className="text-lg font-semibold text-slate-800 mb-4">Email Logs</h3>

        <div className="flex flex-wrap gap-4 mb-6">
          <div className="relative flex-1 min-w-[200px]">
            <SafeIcon icon={FiSearch} className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <input
              type="text"
              placeholder="Search by recipient or subject..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && loadLogs()}
              className="w-full pl-10 pr-4 py-2.5 border border-gray-200 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
            />
          </div>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="px-4 py-2.5 border border-gray-200 rounded-xl focus:ring-2 focus:ring-emerald-500"
          >
            {STATUS_OPTIONS.map((o) => (
              <option key={o.value || 'all'} value={o.value}>{o.label}</option>
            ))}
          </select>
          <input
            type="date"
            value={dateFrom}
            onChange={(e) => setDateFrom(e.target.value)}
            className="px-4 py-2.5 border border-gray-200 rounded-xl focus:ring-2 focus:ring-emerald-500"
          />
          <input
            type="date"
            value={dateTo}
            onChange={(e) => setDateTo(e.target.value)}
            className="px-4 py-2.5 border border-gray-200 rounded-xl focus:ring-2 focus:ring-emerald-500"
          />
          <button
            onClick={() => loadLogs()}
            className="px-4 py-2.5 bg-emerald-600 text-white rounded-xl hover:bg-emerald-700 transition-colors flex items-center gap-2"
          >
            <SafeIcon icon={FiFilter} className="w-4 h-4" />
            Apply
          </button>
        </div>

        {loading ? (
          <div className="flex justify-center py-12">
            <div className="w-10 h-10 border-2 border-emerald-500 border-t-transparent rounded-full animate-spin" />
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-200 text-left text-slate-600">
                  <th className="pb-3 pr-4">Recipient</th>
                  <th className="pb-3 pr-4">Subject</th>
                  <th className="pb-3 pr-4">Status</th>
                  <th className="pb-3 pr-4">Sent</th>
                  <th className="pb-3 pr-4">Opened</th>
                  <th className="pb-3 pr-4">Clicked</th>
                  <th className="pb-3"></th>
                </tr>
              </thead>
              <tbody>
                {logs.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="py-8 text-center text-slate-500">
                      No logs found.
                    </td>
                  </tr>
                ) : (
                  logs.map((log) => (
                    <tr
                      key={log.id}
                      className="border-b border-gray-100 hover:bg-slate-50/50 transition-colors"
                    >
                      <td className="py-3 pr-4 font-medium text-slate-800">{log.recipient_email}</td>
                      <td className="py-3 pr-4 text-slate-600 max-w-[200px] truncate">{log.subject}</td>
                      <td className="py-3 pr-4">
                        <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${
                          log.status === 'sent' ? 'bg-emerald-100 text-emerald-800' :
                          log.status === 'opened' ? 'bg-blue-100 text-blue-800' :
                          log.status === 'clicked' ? 'bg-purple-100 text-purple-800' :
                          'bg-red-100 text-red-800'
                        }`}>
                          {log.status || '—'}
                        </span>
                      </td>
                      <td className="py-3 pr-4 text-slate-500">{formatDate(log.sent_at)}</td>
                      <td className="py-3 pr-4 text-slate-500">{formatDate(log.opened_at)}</td>
                      <td className="py-3 pr-4 text-slate-500">{formatDate(log.clicked_at)}</td>
                      <td className="py-3">
                        <button
                          onClick={() => openDetail(log.id)}
                          className="p-2 text-slate-400 hover:text-emerald-600 hover:bg-emerald-50 rounded-lg transition-colors"
                          title="View details"
                        >
                          <SafeIcon icon={FiEye} className="w-4 h-4" />
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        )}

        {total > pageSize && (
          <div className="mt-4 flex justify-between items-center">
            <p className="text-sm text-slate-500">
              Showing {page * pageSize + 1}–{Math.min((page + 1) * pageSize, total)} of {total}
            </p>
            <div className="flex gap-2">
              <button
                disabled={page === 0}
                onClick={() => setPage((p) => p - 1)}
                className="px-3 py-1.5 rounded-lg border border-gray-200 disabled:opacity-50 hover:bg-gray-50"
              >
                Previous
              </button>
              <button
                disabled={(page + 1) * pageSize >= total}
                onClick={() => setPage((p) => p + 1)}
                className="px-3 py-1.5 rounded-lg border border-gray-200 disabled:opacity-50 hover:bg-gray-50"
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>

      {selectedLog && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50" onClick={() => setSelectedLog(null)}>
          <div
            className="bg-white rounded-2xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-hidden flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="p-6 border-b border-gray-100 flex justify-between items-start">
              <h3 className="text-lg font-semibold text-slate-800">Email details</h3>
              <button
                onClick={() => setSelectedLog(null)}
                className="p-2 text-slate-400 hover:text-slate-600 rounded-lg"
              >
                ×
              </button>
            </div>
            <div className="p-6 overflow-y-auto flex-1 text-sm space-y-3">
              <p><span className="font-medium text-slate-600">To:</span> {selectedLog.recipient_email}</p>
              <p><span className="font-medium text-slate-600">Subject:</span> {selectedLog.subject}</p>
              <p><span className="font-medium text-slate-600">Status:</span> {selectedLog.status}</p>
              <p><span className="font-medium text-slate-600">Sent:</span> {formatDate(selectedLog.sent_at)}</p>
              {selectedLog.opened_at && <p><span className="font-medium text-slate-600">Opened:</span> {formatDate(selectedLog.opened_at)}</p>}
              {selectedLog.clicked_at && <p><span className="font-medium text-slate-600">Clicked:</span> {formatDate(selectedLog.clicked_at)}</p>}
              {selectedLog.email_queue?.body && (
                <div className="mt-4 pt-4 border-t border-gray-100">
                  <p className="font-medium text-slate-600 mb-2">Body preview</p>
                  <div className="bg-slate-50 rounded-lg p-4 max-h-60 overflow-y-auto prose prose-sm max-w-none" dangerouslySetInnerHTML={{ __html: selectedLog.email_queue.body }} />
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {detailLoading && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/20">
          <div className="w-10 h-10 border-2 border-emerald-500 border-t-transparent rounded-full animate-spin" />
        </div>
      )}
    </motion.div>
  );
};

export default MarketingEmailLogs;
